import { Icon } from '@iconify/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { completeResetPassword } from '~features/reset-password/api'
import { CompleteResetPasswordPayload } from '~features/reset-password/types'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '~in-app/components'
import { resetPasswordValidation } from '~in-app/validation/resetPassword.validation'

export const Route = createFileRoute('/(public)/_public/reset-password')({
  component: ResetPasswordPage,
  staticData: {
    privileges: [],
  },
})

function ResetPasswordPage() {
  const [isVisible, toggleVisibility] = useState(false)
  const search = Route.useSearch() as { key?: string }
  const { key: tanstackKey } = search
  const searchParams = new URLSearchParams(window.location.search)
  const key = searchParams.get('key')
  const navigate = useNavigate()
  const resetPasswordRequestMutation = useMutation({
    mutationFn: async (value: CompleteResetPasswordPayload) => {
      const response = await completeResetPassword(value)
      // If API returns null (both success/error cases), throw error to trigger onError
      if (!response) {
        throw new Error('Operation failed - no response received')
      }
      return response
    },
    onSuccess: () => {
      toast.success('Password successfully reset!', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      navigate({ to: '/sign-in' })
    },
    onError: (error: Error) => {
      console.error('Error occurred:', error)
    },
  })
  const formOpts = formOptions({
    defaultValues: {
      newPassword: '',
      key: '',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      resetPasswordRequestMutation.mutate(value)
    },
  })
  const formControl = useForm(formOpts)

  useEffect(() => {
    if (key) {
      formControl.setFieldValue('key', key)
    }
  }, [key])
  return (
    <div className="relative z-10 flex flex-col gap-4">
      <Card className="w-full min-w-[450px]">
        <CardHeader>
          <CardTitle className="text-2xl">Reset Password</CardTitle>
        </CardHeader>

        <form
          noValidate
          onSubmit={
            (e) => {
              e.preventDefault()
              formControl.handleSubmit()
            }
          }
        >
          <CardContent className="grid gap-4">
            <div className="mt-2 grid gap-2">
              <Label
                required
                className="text-sm"
                htmlFor="newPassword"
              >
                New Password
              </Label>

              {/* Use the Field component to handle rendering */}
              <formControl.Field
                name="newPassword"
                validators={
                  {
                    onChange: resetPasswordValidation.shape.newPassword,
                  }
                }
              >
                {
                  field => (
                    <>
                      <div className="relative flex w-full">
                        <Input
                          className="pr-10"
                          id="newPassword"
                          type={isVisible ? 'text' : 'password'}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={e => field.handleChange(e.target.value)}
                        />

                        {/* Placeholder for the eye toggle button logic */}
                        <button
                          className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-sm"
                          type="button"
                          onClick={() => toggleVisibility(!isVisible)}
                        >
                          <Icon
                            height={24}
                            icon={isVisible ? 'mdi:eye' : 'mdi:eye-off'}
                            width={24}
                          />
                        </button>
                      </div>

                      {
                        field.state.meta.errors && (
                          <em
                            className="error-message"
                            role="alert"
                          >
                            {field.state.meta.errors.join(', ')}
                          </em>
                        )
                      }
                    </>
                  )
                }
              </formControl.Field>
            </div>
          </CardContent>

          <CardFooter>
            <Button className="w-full">Save</Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
